import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useBrand } from "@/contexts/BrandContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { toast } from "sonner";
import { Loader2, Phone, ShieldCheck } from "lucide-react";
import { hideOnError } from "@/lib/assets";

const RESEND_SECONDS = 30;

export default function ClientLogin() {
  const { clientLogin } = useAuth();
  const { brand, logoSrc } = useBrand();
  const navigate = useNavigate();
  const [step, setStep] = useState("phone");
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendSec, setResendSec] = useState(0);
  const timerRef = useRef(null);

  // Countdown that decrements resendSec every 1s until it hits 0
  useEffect(() => {
    if (resendSec <= 0) { if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; } return; }
    if (!timerRef.current) {
      timerRef.current = setInterval(() => {
        setResendSec((s) => {
          if (s <= 1) { clearInterval(timerRef.current); timerRef.current = null; return 0; }
          return s - 1;
        });
      }, 1000);
    }
    return () => { if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; } };
  }, [resendSec]);

  const startResendTimer = () => setResendSec(RESEND_SECONDS);

  const requestOtp = async (e) => {
    if (e) e.preventDefault();
    if (resendSec > 0) return; // guard against rapid clicks
    setLoading(true);
    try {
      const r = await api.post("/auth/otp/request", { phone });
      const mock = r.data?.mock;
      const emailSent = r.data?.email_sent;
      const emailAvailable = r.data?.email_available;
      if (mock) toast.success("OTP sent (check backend logs — SMS mocked)");
      else if (emailSent) toast.success(`OTP sent to your phone${emailAvailable ? " and email" : ""}`);
      else toast.success("OTP sent to your phone");
      setStep("otp");
      startResendTimer();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const verify = async () => {
    setLoading(true);
    try {
      const r = await api.post("/auth/otp/verify", { phone, code });
      await clientLogin(r.data.token, r.data.client);
      toast.success("Welcome!");
      navigate("/client/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid OTP");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-background via-background to-accent/5">
      <Card className="w-full max-w-md p-8 border-border">
        <div className="mb-6 text-center">
          {logoSrc ? (
            <img src={logoSrc} alt={brand.app_name} className="mx-auto h-14 object-contain mb-3" onError={hideOnError} />
          ) : (
            <div className="mx-auto h-12 w-12 rounded-xl bg-accent/10 text-accent flex items-center justify-center mb-3">
              {step === "phone" ? <Phone className="h-5 w-5" /> : <ShieldCheck className="h-5 w-5" />}
            </div>
          )}
          <div className="text-xs font-semibold uppercase tracking-[0.15em] text-accent mb-1">{brand.client_portal_name || "Client Portal"}</div>
          <h2 className="text-3xl font-bold">{step === "phone" ? "Sign in" : "Verify OTP"}</h2>
          <p className="text-muted-foreground mt-2 text-sm">
            {step === "phone" ? "Enter your registered mobile number" : `We sent a 6-digit code to ${phone}`}
          </p>
        </div>

        {step === "phone" ? (
          <form onSubmit={requestOtp} className="space-y-4">
            <div>
              <Label htmlFor="phone">Mobile number</Label>
              <Input
                id="phone" required value={phone} placeholder="Enter mobile number"
                onChange={(e) => setPhone(e.target.value)}
                data-testid="client-phone-input" className="mt-1 h-11"
              />
              <p className="text-xs text-muted-foreground mt-1">Must be your registered primary number</p>
            </div>
            <Button
              type="submit" disabled={loading}
              className="w-full h-11 bg-accent text-accent-foreground hover:opacity-90"
              data-testid="client-send-otp-btn"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send OTP"}
            </Button>
          </form>
        ) : (
          <div className="space-y-6">
            <div className="flex justify-center">
              <InputOTP maxLength={6} value={code} onChange={setCode} data-testid="otp-input">
                <InputOTPGroup>
                  {[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} />)}
                </InputOTPGroup>
              </InputOTP>
            </div>
            <Button
              onClick={verify} disabled={loading || code.length !== 6}
              className="w-full h-11 bg-accent text-accent-foreground hover:bg-accent/90"
              data-testid="client-verify-btn"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify & Login"}
            </Button>
            <div className="text-center">
              <button
                type="button"
                onClick={() => requestOtp()}
                disabled={loading || resendSec > 0}
                className="text-xs text-muted-foreground hover:text-foreground disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="resend-otp-btn"
              >
                {resendSec > 0
                  ? `Resend OTP in ${resendSec}s`
                  : "Didn't get it? Resend OTP"}
              </button>
            </div>
            <button
              onClick={() => { setStep("phone"); setCode(""); setResendSec(0); }}
              className="w-full text-xs text-muted-foreground hover:text-foreground"
              data-testid="change-number-btn"
            >
              ← Change number
            </button>
          </div>
        )}

        <div className="mt-6 pt-6 border-t border-border text-center">
          <span className="text-xs text-muted-foreground">Need help? Contact your account manager.</span>
        </div>
      </Card>
    </div>
  );
}
